import { separate } from '@packages/strings';
import React from 'react';
import styled from 'styled-components';
import { cleanProps } from '../utils';

/**
 * Create an auto props cleaning component that will return a component that will
 * pass all given props except `cleaningProperties` properties
 *
 * @example
 * // Example 1 using it directly as a style factory (It can loose Editor syntax)
 * const Style = createdStyled('div', ['background'], true)`
 * & {
 *      background: ${({background}) => (background)};
 * }
 * `;
 *
 * // Example 2 using function as factory style (which receive an object {
 *     css: styledComponent
 * }), supporting css syntax
 * const Style = createdStyled('div', ['background'], ({css}) => css`
 * & {
 *      background: ${({background}) => (background)};
 * }
 * `);
 *
 * // Example 3 using it in directly as Component
 * const Style = styled(createdStyled('div', ['background']))`
 * & {
 *      background: ${({background}) => (background)};
 * }
 * `
 * @author Dumitrana Alinus <alinus@wooter.co>
 * @param Component
 * @param cleanProperties
 * @param style
 * @deprecated In favor of native support of styled-components to remove properties
 * @returns {function(
 *  component:string|React.ComponentType,
 *  cleanProperties: string|string[],
 *  style: boolean|function(styledComponent:any): any
 * ): React.ComponentType}
 */
export function createStyled<T = Record<string, any>>(Component: any, cleanProperties: string | string[], style?: any) {
    const ComponentName =
        typeof Component !== 'string'
            ? Component.displayName || Component.name || Component.constructor.name || 'Unknown'
            : Component;

    function Component$(props: T): any {
        const props$ = { ...props };

        if (typeof cleanProperties === 'string') {
            cleanProperties = separate(cleanProperties, ',');
        }

        cleanProperties.forEach((p: any) => {
            if (p && typeof p.clean === 'function') {
                p = p.clean;
            }

            if (p && typeof p === 'function') {
                p(props$);
            }
        });

        cleanProps(
            props$,
            cleanProperties.filter((p: any) => p && (typeof p !== 'function' || !p.clean)),
        );

        return <Component {...props$} />;
    }

    Component$.displayName = `createStyled(${ComponentName})`;

    if (style) {
        const css = styled(Component$);

        // If style it's a function it will pass `{ css: styledComponent }` to `style` function
        // and return the result.
        if (typeof style === 'function') {
            return style({ css });
        }

        // else will return `css` to use like
        // const Style = createStyled('div', [])`&{ background: #fff; }`

        return css;
    }

    // if there's no style argument, pass Component
    return Component$;
}
